import React, { useState, useEffect } from 'react';
import { ArrowLeft, RotateCcw, Play, CheckCircle, XCircle, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const FunctionMachineGame = () => {
    const navigate = useNavigate();
    const [level, setLevel] = useState(1);
    const [rule, setRule] = useState({ type: 'std', m: 1, b: 0, d: 1 }); // Unified rule object
    const [history, setHistory] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [isAnimating, setIsAnimating] = useState(false);
    const [animValue, setAnimValue] = useState(null); 
    const [score, setScore] = useState(0);
    const [message, setMessage] = useState(null);

    const [userEquation, setUserEquation] = useState([]);
    const [evalError, setEvalError] = useState(null);

    // Helper to calculate y based on rule type
    const calculateOutput = (r, x) => {
        if (r.type === 'std') return r.m * x + r.b;
        if (r.type === 'div') return (x / r.d) + r.b;
        if (r.type === 'paren') return r.m * (x + r.b); // m(x+b)
        return x;
    };

    const generateNewRound = (currLevel) => {
        let newRule = { type: 'std', m: 1, b: 0, d: 1 };
        
        // Ensure robustness by defaulting logic
        try {
            switch(currLevel) {
                case 1: // Add/Sub
                    newRule.type = 'std';
                    newRule.m = 1;
                    do { newRule.b = Math.floor(Math.random() * 20) - 10; } while (newRule.b === 0);
                    break;
                case 2: // Mult or Div
                    if (Math.random() > 0.5) {
                        newRule.type = 'std';
                        newRule.b = 0;
                        do { newRule.m = Math.floor(Math.random() * 9) + 2; } while (newRule.m === 1);
                    } else {
                        newRule.type = 'div';
                        newRule.b = 0;
                        newRule.d = [2, 4, 5, 10][Math.floor(Math.random() * 4)];
                    }
                    break;
                case 3: // Two-step Linear (mx + b)
                    newRule.type = 'std';
                    do { newRule.m = Math.floor(Math.random() * 5) + 2; } while (newRule.m === 0);
                    do { newRule.b = Math.floor(Math.random() * 20) - 10; } while (newRule.b === 0);
                    break;
                case 4: // Two-step Division (x/d + b)
                    newRule.type = 'div';
                    newRule.d = [2, 4, 5, 10][Math.floor(Math.random() * 4)];
                    do { newRule.b = Math.floor(Math.random() * 20) - 10; } while (newRule.b === 0);
                    break;
                case 5: // Parentheses m(x+b)
                    newRule.type = 'paren';
                    do { newRule.m = Math.floor(Math.random() * 5) + 2; } while (newRule.m === 1);
                    do { newRule.b = Math.floor(Math.random() * 10) - 5; } while (newRule.b === 0);
                    break;
                default:
                    newRule = { type: 'std', m: 1, b: 5, d: 1 }; // Fallback
                    break;
            }
        } catch (e) {
            console.error("Error generating rule:", e);
            newRule = { type: 'std', m: 1, b: 1, d: 1 };
        }

        setRule(newRule);
        setHistory([]);
        setInputValue("");
        setMessage(null);
        setUserEquation([]);
        setEvalError(null);
    };

    const handleEquationInput = (token) => {
        if (token === 'clear') {
            setUserEquation([]);
        } else if (token === 'back') {
            setUserEquation(prev => prev.slice(0, -1));
        } else {
            setUserEquation(prev => [...prev, token]);
        }
        setEvalError(null);
    };

    const checkEquation = () => {
        let eqString = userEquation.join("");
        
        if (!/^[0-9x+\-*/(). ]+$/.test(eqString)) {
            setEvalError("Invalid characters");
            return;
        }

        try {
            const testPoints = [0, 4, 10, 20, 100]; // Use friendly numbers for divisions
            let allCorrect = true;

            for (let x of testPoints) {
                const expression = eqString.replace(/x/g, `(${x})`);
                // eslint-disable-next-line
                const userY = Function(`"use strict"; return (${expression})`)();
                const correctY = calculateOutput(rule, x);
                
                if (Math.abs(userY - correctY) > 0.001) {
                    allCorrect = false;
                    break;
                }
            }

            if (allCorrect) {
                 setMessage({ type: 'success', text: "Correct! You cracked the code." });
                 setScore(score + 20);
                 setTimeout(() => {
                    if (score >= 40 * level && level < 5) setLevel(level + 1); // Progression threshold
                    else generateNewRound(level);
                 }, 2000);
            } else {
                 setMessage({ type: 'error', text: "That doesn't match the data. Try again!" });
                 setScore(Math.max(0, score - 5));
            }

        } catch (err) {
            setEvalError("Invalid Equation Syntax");
        }
    };

    useEffect(() => {
        generateNewRound(level);
    }, [level]);

    const handleInputTest = (e) => {
        e.preventDefault();
        if (inputValue === "" || isAnimating) return;
        const x = parseInt(inputValue);
        if (isNaN(x)) return;

        setIsAnimating(true);
        setAnimValue(x);
        
        setTimeout(() => {
            const y = calculateOutput(rule, x);
            setHistory(prev => [...prev, { input: x, output: parseFloat(y.toFixed(2)) }]); // Clean decimals
            setInputValue("");
            setIsAnimating(false);
            setAnimValue(null);
        }, 1500); 
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center py-8">
            <div className="w-full max-w-4xl px-4 flex flex-col md:flex-row justify-between items-center mb-6 gap-4 md:gap-0">
                 <button onClick={() => navigate("/games")} className="flex items-center text-gray-600 self-start md:self-auto">
                    <ArrowLeft className="mr-1" /> Back to Games
                 </button>
                 <h1 className="text-2xl md:text-3xl font-bold text-gray-800 text-center">The Function Machine</h1>
                 <div className="flex items-center gap-4">
                     <span className="text-sm font-semibold text-purple-600 bg-purple-100 px-3 py-1 rounded-full">Level {level}</span>
                     <span className="text-lg font-bold text-gray-700">Score: {score}</span>
                 </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-5xl">
                
                {/* Left: The Machine (Same as before) */}
                <div className="bg-white p-6 rounded-xl shadow-lg flex flex-col items-center relative min-h-[500px]">
                    <h2 className="text-xl font-bold text-gray-700 mb-6">Input Test</h2>
                    <form onSubmit={handleInputTest} className="w-full max-w-xs flex gap-2 mb-8 relative z-20">
                        <input 
                            type="number" 
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder="Enter x"
                            className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                            autoFocus
                        />
                        <button 
                            type="submit" 
                            disabled={isAnimating || inputValue === ""}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-700 disabled:bg-gray-300"
                        >
                            <Play size={20} />
                        </button>
                    </form>

                    {/* SVG Machine */}
                    <svg width="250" height="250" viewBox="0 0 300 300" className="mt-4">
                        <rect x="75" y="80" width="150" height="140" rx="10" fill="#e2e8f0" stroke="#64748b" strokeWidth="4" />
                        <text x="150" y="120" textAnchor="middle" fill="#64748b" fontFamily="monospace" fontSize="14">FUNCTION</text>
                        <circle cx="150" cy="170" r="30" fill="#cbd5e1" stroke="#94a3b8" strokeWidth="2" />
                        <g transform="translate(150, 170)">
                            <path 
                                d="M-15,-5 L-15,5 L-5,5 L-5,15 L5,15 L5,5 L15,5 L15,-5 L5,-5 L5,-15 L-5,-15 L-5,-5 Z" 
                                fill="#475569"
                                className={isAnimating ? "animate-spin-slow origin-center" : ""} 
                                style={{ transformOrigin: 'center', animationDuration: '1.5s' }}
                            />
                        </g>
                        <path d="M 100 80 L 70 20 L 230 20 L 200 80 Z" fill="#94a3b8" />
                        <path d="M 120 220 L 120 260 L 180 260 L 180 220 Z" fill="#94a3b8" />
                        {isAnimating && animValue !== null && (
                            <g className="animate-function-drop">
                                <circle cx="150" cy="40" r="20" fill="#3b82f6" />
                                <text x="150" y="45" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">{animValue}</text>
                            </g>
                        )}
                    </svg>

                    <style>{`
                        @keyframes drop {
                            0% { transform: translateY(0); opacity: 1; }
                            30% { transform: translateY(130px); opacity: 1; }
                            70% { transform: translateY(130px); opacity: 1; }
                            100% { transform: translateY(240px); opacity: 0; }
                        }
                        .animate-function-drop { animation: drop 1.5s linear forwards; }
                        .animate-spin-slow { animation: spin 3s linear infinite; }
                        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                    `}</style>
                </div>

                {/* Right: Data & Equation Builder */}
                <div className="flex flex-col gap-6">
                    {/* Table */}
                    <div className="bg-white p-4 rounded-xl shadow-lg border-2 border-blue-100 flex-1 max-h-60 overflow-hidden flex flex-col">
                        <h2 className="text-lg font-bold text-gray-700 mb-2 text-center">Data Log</h2>
                        <div className="w-full overflow-y-auto flex-1">
                            <table className="w-full text-center border-collapse text-sm">
                                <thead className="bg-blue-50 text-blue-800 sticky top-0">
                                    <tr>
                                        <th className="p-2 border-b">Input (x)</th>
                                        <th className="p-2 border-b">Output (y)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.length === 0 ? (
                                        <tr><td colSpan="2" className="p-4 text-gray-400 italic">No inputs yet</td></tr>
                                    ) : (
                                        history.map((row, idx) => (
                                            <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                                                <td className="p-2 font-mono">{row.input}</td>
                                                <td className="p-2 font-mono font-bold text-blue-600">{row.output}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Expression Builder */}
                    <div className="bg-purple-50 p-6 rounded-xl border-2 border-purple-200">
                        <h2 className="text-xl font-bold text-purple-900 mb-4 text-center">Build the Rule</h2>
                        
                        {/* Display Screen */}
                        <div className="bg-white border-2 border-purple-300 rounded-lg p-4 mb-4 min-h-[60px] flex items-center text-2xl font-mono text-gray-800 shadow-inner overflow-x-auto">
                            <span className="text-gray-400 mr-2 select-none">y = </span>
                            {userEquation.join(" ")}
                            <span className="w-0.5 h-6 bg-purple-500 animate-pulse ml-1"></span>
                        </div>
                        {evalError && <p className="text-red-500 text-sm mb-2 text-center">{evalError}</p>}

                        {/* Keypad */}
                        <div className="grid grid-cols-5 gap-2 select-none">
                            {/* Variables & Operators */}
                            <button onClick={() => handleEquationInput('x')} className="bg-blue-100 text-blue-800 font-bold p-3 rounded hover:bg-blue-200">x</button>
                            <button onClick={() => handleEquationInput('+')} className="bg-orange-100 text-orange-800 font-bold p-3 rounded hover:bg-orange-200">+</button>
                            <button onClick={() => handleEquationInput('-')} className="bg-orange-100 text-orange-800 font-bold p-3 rounded hover:bg-orange-200">-</button>
                            <button onClick={() => handleEquationInput('*')} className="bg-orange-100 text-orange-800 font-bold p-3 rounded hover:bg-orange-200">×</button>
                            <button onClick={() => handleEquationInput('/')} className="bg-orange-100 text-orange-800 font-bold p-3 rounded hover:bg-orange-200">÷</button>
                            
                            {/* Numbers 1-5 */}
                            {[1,2,3,4,5].map(n => (
                                <button key={n} onClick={() => handleEquationInput(n)} className="bg-white text-gray-700 font-bold p-3 rounded shadow hover:bg-gray-50">{n}</button>
                            ))}
                            
                            {/* Numbers 6-0 */}
                            {[6,7,8,9,0].map(n => (
                                <button key={n} onClick={() => handleEquationInput(n)} className="bg-white text-gray-700 font-bold p-3 rounded shadow hover:bg-gray-50">{n}</button>
                            ))}

                            {/* Controls */}
                            <button onClick={() => handleEquationInput('(')} className="bg-gray-200 text-gray-800 font-bold p-3 rounded hover:bg-gray-300">(</button>
                            <button onClick={() => handleEquationInput(')')} className="bg-gray-200 text-gray-800 font-bold p-3 rounded hover:bg-gray-300">)</button>
                            
                            <button onClick={() => handleEquationInput('back')} className="bg-red-100 text-red-800 font-bold p-3 rounded hover:bg-red-200 col-span-1">DEL</button>
                            <button onClick={() => handleEquationInput('clear')} className="bg-red-100 text-red-800 font-bold p-3 rounded hover:bg-red-200 col-span-2">CLR</button>
                        </div>
                        
                        <button 
                            onClick={checkEquation}
                            className="w-full mt-4 bg-purple-600 text-white font-bold py-3 rounded-lg shadow hover:bg-purple-700 transition-colors"
                        >
                            Check Rule
                        </button>
                    </div>

                    {/* Message Area */}
                    {message && (
                        <div className={`
                            p-4 rounded-lg flex items-center justify-center gap-2 font-bold animate-bounce-in
                            ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                        `}>
                            {message.type === 'success' ? <CheckCircle /> : <XCircle />}
                            {message.text}
                        </div>
                    )}
                </div>

            </div>

            <div className="mt-8 bg-purple-50 p-6 rounded-xl border border-purple-200 w-full max-w-4xl">
                <h3 className="text-lg font-bold text-purple-900 mb-4 flex items-center gap-2">
                    <Info size={20}/> Game Rules & Levels
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-xs text-purple-800">
                    <div>
                        <span className="font-bold block mb-1">Level 1: +/-</span>
                        <p>Add/Subtract</p>
                    </div>
                    <div>
                        <span className="font-bold block mb-1">Level 2: ×/÷</span>
                        <p>Multiply/Divide</p>
                    </div>
                    <div>
                        <span className="font-bold block mb-1">Level 3: Mix</span>
                        <p>y = mx + b</p>
                    </div>
                    <div>
                        <span className="font-bold block mb-1">Level 4: Combos</span>
                        <p>Division & Add</p>
                    </div>
                    <div>
                        <span className="font-bold block mb-1">Level 5: ( )</span>
                        <p>Parentheses</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FunctionMachineGame;
